/**
 * A workspace's brand before anybody has signed in.
 *
 * The login screen has no session, so it cannot use `/api/branding/theme`. Which
 * workspace a visitor is looking at comes from the hostname —
 * `example.domain.com` is the workspace `example` — with `?w=` as an explicit
 * override for local development and for support.
 *
 * Resolution happens on the server. Which suffix is the portal domain is the
 * platform's configuration, not something a bundled SPA should be hardcoding,
 * and the previous version of this file counted labels instead: it read `acme`
 * out of `acme.someone-elses-site.com` and looked up a workspace called `www`.
 *
 * Precedence is deliberate — `?w=`, then the host, then the last workspace this
 * browser signed into. The stored slug used to come first, which meant the bare
 * portal domain showed whichever workspace you happened to visit last.
 *
 * Fails silently by design. An unknown host, a network error or a workspace that
 * has configured nothing all leave the product's own brand on display: a sign-in
 * page that cannot render because branding failed is a far worse outcome than
 * one that renders unbranded.
 */
import { useEffect, useState } from 'react';
import { api } from '../services/api';

export const usePublicBranding = () => {
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    // `?w=` is an explicit override and wins outright. Otherwise the host
    // decides, and the last workspace this browser signed into is the fallback
    // for when it does not — which is the bare portal domain, and nowhere else.
    // Only the server knows whether a host resolves, so both are sent and it
    // applies that order rather than the browser guessing.
    const override = new URLSearchParams(window.location.search).get('w');
    const query = new URLSearchParams({
      host: window.location.hostname || '',
      slug: window.localStorage.getItem('hrms.lastWorkspaceSlug') || '',
    });

    let cancelled = false;
    api
      .get(override
        ? `/api/branding/public/${encodeURIComponent(override)}`
        : `/api/branding/public?${query}`)
      .then(({ data }) => {
        if (cancelled || !data) return;
        setBranding(data);
        if (data.app_name) document.title = data.app_name;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return branding;
};

export default usePublicBranding;
